import {
  Button,
  DocumentList,
  RadioButton,
  RadioGroup,
  Select,
  SelectData,
  TextArea,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, FormGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import useInput from "../../../hooks/input/useInput";
import QuestionDataTypeEnum from "../../../models/Questionnaire/QuestionDataTypeEnum";
import QuestionDisplayTypeEnum from "../../../models/Questionnaire/QuestionDisplayTypeEnum";
import QuestionnaireAnswer from "../../../models/Questionnaire/QuestionnaireAnswer";
import QuestionnaireQuestion from "../../../models/Questionnaire/QuestionnaireQuestion";
import InitialConversationQuestionDataItem from "../../../models/Request/InitialConversationQuestionDataItem";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import { useInitialConversationDocuments } from "./useInitialConversationDocuments";

interface Props {
  allDocuments: RequestDocumentData[];
  stateIndex: number;
  question: QuestionnaireQuestion;
  conversationItem: InitialConversationQuestionDataItem;
  errorText?: string;
  setState: (prevState: any) => void;
  openDocumentsModal?: boolean;
  onCloseDocumentsModal: () => void;
  onConfirmDocumentsModal: (
    documents: RequestDocumentData[],
    stateIndex: number
  ) => void;
  onRemoveDocumentFromList: (
    stateIndex: number,
    document: RequestDocumentData
  ) => void;
  handleToggleDocumentsModal: () => void;
}

export default function QuestionComponentFactory({
  allDocuments,
  stateIndex,
  question,
  conversationItem,
  errorText,
  setState,
  openDocumentsModal = false,
  onCloseDocumentsModal,
  onConfirmDocumentsModal,
  onRemoveDocumentFromList,
  handleToggleDocumentsModal,
}: Props) {
  const { getIndexedName, handleArrayChange } = useInput();

  const { t } = useTranslation();

  const {
    handleCloseDocumentsModal,
    handleConfirmDocumentsModal,
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleRemoveDocument,
    handleRemoveDocumentFromList,
    handleDownloadDocument,
  } = useInitialConversationDocuments({
    stateIndex: stateIndex,
    conversationItem: conversationItem,
    onCloseDocumentsModal: onCloseDocumentsModal,
    onConfirmDocumentsModal: onConfirmDocumentsModal,
    onRemoveDocumentFromList: onRemoveDocumentFromList,
  });

  const resolveTextFieldType = (displayType: QuestionDataTypeEnum) => {
    switch (displayType) {
      case QuestionDataTypeEnum.Integer:
        return "number";
      case QuestionDataTypeEnum.Text:
      default:
        return "text";
    }
  };
  switch (question.displayTypeId) {
    case QuestionDisplayTypeEnum.ReadOnly:
    case QuestionDisplayTypeEnum.Text:
      return (
        <>
          {(conversationItem.isVisible || conversationItem.isRepeatable) && (
            <TextField
              name={getIndexedName(
                stateIndex,
                nameof<typeof conversationItem>("answer")
              )}
              type={resolveTextFieldType(question.dataTypeId)}
              value={conversationItem.answer ?? undefined}
              label={question.name}
              disabled={
                question.displayTypeId === QuestionDisplayTypeEnum.ReadOnly
              }
              required={question.isRequired}
              maxLength={question.answerLength}
              onChange={(e) => handleArrayChange(e, stateIndex, setState)}
              errorText={errorText}
            />
          )}
        </>
      );
    case QuestionDisplayTypeEnum.TextArea:
      return (
        <TextArea
          name={getIndexedName(
            stateIndex,
            nameof<typeof conversationItem>("answer")
          )}
          value={conversationItem.answer ?? undefined}
          label={question.name}
          required={question.isRequired}
          maxLength={question.answerLength}
          rows={4}
          onChange={(e) => handleArrayChange(e, stateIndex, setState)}
          errorText={errorText}
          ariaLabel={question.name}
        />
      );
    case QuestionDisplayTypeEnum.Select:
      return (
        <Select
          name={getIndexedName(
            stateIndex,
            nameof<typeof conversationItem>("answerId")
          )}
          value={conversationItem.answerId ?? ""}
          label={question.name}
          required={question.isRequired}
          selectData={
            {
              valueProp: "id",
              textProp: "answer",
              data: [
                { id: 0, answer: "" } as QuestionnaireAnswer,
                ...question.answers,
              ],
            } as SelectData<QuestionnaireAnswer>
          }
          onChange={(e) => handleArrayChange(e, stateIndex, setState)}
          errorText={errorText}
        />
      );
    case QuestionDisplayTypeEnum.MultiSelect:
      return (
        <Select
          name={getIndexedName(
            stateIndex,
            nameof<typeof conversationItem>("answerIds")
          )}
          value={conversationItem.answerIds ?? []}
          label={question.name}
          required={question.isRequired}
          multiple
          selectData={
            {
              valueProp: "id",
              textProp: "answer",
              data: [...question.answers],
            } as SelectData<QuestionnaireAnswer>
          }
          onChange={(e) => handleArrayChange(e, stateIndex, setState)}
          errorText={errorText}
        />
      );
    case QuestionDisplayTypeEnum.Radio:
      return (
        <RadioGroup
          name={getIndexedName(
            stateIndex,
            nameof<typeof conversationItem>("answerId")
          )}
          title={question.name}
          required={question.isRequired}
          onChange={(e) => handleArrayChange(e, stateIndex, setState)}
          errorText={errorText}
        >
          {question.answers.map((a) => (
            <RadioButton
              key={a.id}
              label={a.answer}
              value={a.id}
              checked={+(conversationItem?.answerId ?? 0) === a.id}
              ariaLabel={t("accessibility.radioAnswer")}
            />
          ))}
        </RadioGroup>
      );
    case QuestionDisplayTypeEnum.Upload:
      return (
        <FormGroup>
          <Row>
            <Col md={12}>
              <Button
                color="primary"
                disabled={
                  conversationItem.documents &&
                  conversationItem.documents.length >= 10
                    ? true
                    : false
                }
                outline
                icon={<UploadIcon />}
                onClick={() => handleToggleDocumentsModal()}
              >
                {t("documentUpload.additionalDocumentationAttach")}
              </Button>
              <Typography variant="small" color="danger">
                {errorText}
              </Typography>
            </Col>
          </Row>
          <DocumentsModal
            open={openDocumentsModal}
            allDocuments={allDocuments}
            onClose={handleCloseDocumentsModal}
            onConfirm={handleConfirmDocumentsModal}
            onUpload={handleUploadDocuments}
            onRemove={handleRemoveDocument}
            onDownload={handleDownloadDocument}
            onExistingDocumentClick={handleUploadExistingDocument}
          />
          <br />
          {conversationItem.documents &&
          conversationItem.documents.length > 0 ? (
            <Row>
              <Col md={12}>
                <Col md={12}>
                  <Typography variant="smallHeading">
                    {t("documentUpload.additionalDocumentationList")}
                  </Typography>
                </Col>
                <DocumentList
                  documents={conversationItem.documents}
                  onRemoveClick={handleRemoveDocumentFromList}
                  onDocumentClick={handleDownloadDocument}
                  fullWidth
                />
              </Col>
            </Row>
          ) : (
            <></>
          )}
        </FormGroup>
      );
    default:
      return <></>;
  }
}
